import {
  AnchoredPopup,
  EditIcon,
  IconButton,
  useAnchoredPopupAnchor,
} from "@carbon6/c6-frontend-design";
import { useMemo, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import type { TrainingSessionListView } from "@/api";
import { NotePanel, useNoteController, type NoteController } from "@/components/note";
import { HatTextTooltip } from "@/components/tooltip";
import { formatSessionNote, sessionHasNote } from "./trainingSessionFormat";
import styles from "./SessionNoteCell.module.css";

type SessionNoteCellProps = {
  view: TrainingSessionListView;
  // 저장·삭제는 행의 노트 미리보기를 오래된 값으로 만든다. 목록을 다시 받는 일은 source 를 가진 패널이 한다.
  onNoteChanged: () => void;
};

// 목록 행의 노트 칸. 아이콘 하나로 세 가지를 한다: 노트 유무 표시, hover 미리보기, 클릭 편집.
//
// 노트가 없는 행도 트리거를 흐리게 표시한다(빈 칸이 아니다). 빈 칸은 여기서 무엇을 할 수 있는지
// 알리지 못한다. 그러면 사용자는 노트를 처음 쓰려고 상세 창까지 들어가야 한다. 편집 표면은
// 데이터셋 표의 메모 팝오버와 같은 형태다. 본문 편집기는 상세 탭과 같은 NotePanel 을 쓴다.
export function SessionNoteCell({ view, onNoteChanged }: SessionNoteCellProps): ReactNode {
  const popup = useAnchoredPopupAnchor();
  // 닫힌 행에서 불필요한 요청을 보내지 않는다. 미리보기는 목록 응답이 포함한 값으로 표시한다.
  const controller = useNoteController("session", view.id, { enabled: popup.open });

  const hasNote = sessionHasNote(view.note);
  const preview = formatSessionNote(view.note);

  // 저장·삭제가 끝난 뒤에만 목록을 다시 받는다. NotePanel 은 controller 만 알면 되므로
  // 갱신 신호는 여기서 감싼다.
  const listAwareController = useMemo<NoteController>(
    () => ({
      ...controller,
      onSave: async (text: string) => {
        const saved = await controller.onSave(text);
        if (saved) {
          onNoteChanged();
        }
        return saved;
      },
      onClear: async () => {
        const cleared = await controller.onClear();
        if (cleared) {
          onNoteChanged();
        }
        return cleared;
      },
    }),
    [controller, onNoteChanged],
  );

  const label = hasNote ? "노트 보기" : "노트 작성";
  const tooltip = hasNote ? preview : "노트를 작성합니다.";

  return (
    // 행 클릭은 상세 창을 연다. 그래서 트리거 조작이 행으로 전파되지 않게 막는다. 래퍼는 조작
    // 대상이 아니고 전파만 막으므로 role="presentation" 이다. 키보드 경로는 안쪽 버튼이 맡는다.
    <span
      className={styles.cell}
      role="presentation"
      onClick={stopRowClick}
      onPointerDown={stopRowClick}
    >
      {/* 팝업이 열리면 미리보기 툴팁을 끈다. 같은 내용을 두 번 겹쳐 표시하지 않는다. */}
      <HatTextTooltip text={tooltip} placement="bottom" align="end" disabled={popup.open}>
        <IconButton
          aria-label={label}
          size="sm"
          variant="surface"
          selected={popup.open}
          aria-expanded={popup.open}
          aria-haspopup="dialog"
          className={hasNote ? undefined : styles.emptyTrigger}
          onClick={(event: ReactMouseEvent<HTMLButtonElement>) => popup.openFromElement(event)}
        >
          <EditIcon />
        </IconButton>
      </HatTextTooltip>
      <AnchoredPopup
        open={popup.open}
        anchor={popup.anchor}
        onClose={popup.close}
        // 표 오른쪽 끝 칸이라 팝업도 왼쪽으로 펼친다(노트 툴팁과 같은 정렬).
        placement="bottom-end"
        role="dialog"
        aria-label="Session 노트"
        width="22rem"
        maxHeight="28rem"
      >
        <AnchoredPopup.Body padding="md" scroll>
          <NotePanel controller={listAwareController} />
        </AnchoredPopup.Body>
      </AnchoredPopup>
    </span>
  );
}

function stopRowClick(event: ReactMouseEvent<HTMLElement>): void {
  event.stopPropagation();
}
